import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createPromoShopItem,
  getPromoById,
  getPromoShopItems,
  updatePromo,
} from '../../../apis/atlantis.api';
import { getCampaign } from '../../../apis/campaigns.api';
import { useAuth } from '../../../contexts/auth.context';
import ExpandableShopItem from './ShopItem';
import AddShopItemButton from './AddShopItemButton';
import PromoBreadcrumb from './PromoBreadcrumb';
import { getPromoTypeColor, getStatusColor } from '../../terminal/handlers/utils';
import { showUtcClock } from '../../../utils/utcDateTime';
import {
  PromoType,
  VipPromoKind,
  VipTier,
  VipTierLabel,
  VipTierRungs,
  VipTierThresholdsSol,
  canEnableShopItems,
  isShopEnableOnlyWallet,
  isShopReadOnlyWallet,
} from '../../../utils/constants';
import { Button, Form, Spinner } from 'react-bootstrap';
import { getRankOptions, validateLeaderboardRanks } from '../../../utils/promoRankValidation';

const promoSupportsShopItems = promo => {
  if (!promo) {
    return false;
  }

  return (
    // WAITLIST joined the shop-item promos with the creation pair (its
    // items are the wave prize boxes; rewards are forced always-win
    // server-side) — the item forms below already speak isWaitlist.
    [PromoType.FRAGMENT, PromoType.LOSER, PromoType.VIP, PromoType.WAITLIST].includes(
      promo.promoType
    ) || promo.vipPromoKind === VipPromoKind.LEADERBOARD
  );
};

const Promo = () => {
  const navigate = useNavigate();

  const [promo, setPromo] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [promoSettings, setPromoSettings] = useState({
    vipPromoKind: VipPromoKind.TIER,
    vipTier: VipTier.BD,
  });
  const [promoSettingsError, setPromoSettingsError] = useState('');
  const [promoSettingsSaving, setPromoSettingsSaving] = useState(false);
  const { promoId } = useParams();
  const { auth } = useAuth();
  const shopEnableOnly = isShopEnableOnlyWallet(auth?.username);
  const showItemEnable = canEnableShopItems(auth?.username);
  const shopReadOnly = isShopReadOnlyWallet(auth?.username);
  const shopWritesLocked = shopEnableOnly || shopReadOnly;
  const [loading, setLoading] = useState(false);

  const authToken = useRef(auth?.idToken); // Create a ref for the auth?.idToken
  const supportsShopItems = promoSupportsShopItems(promo);
  const isVipPromo = promo?.promoType === PromoType.VIP || promo?.vipPromoKind || promo?.vipTier;
  const isSelectedLeaderboardPromo = promoSettings.vipPromoKind === VipPromoKind.LEADERBOARD;
  const isPersistedLeaderboardPromo = promo?.vipPromoKind === VipPromoKind.LEADERBOARD;
  // Rungs come from the shared ladder (TRU-1116). ALL is leaderboard-only.
  const vipTierOptions = isSelectedLeaderboardPromo ? [...VipTierRungs, VipTier.ALL] : VipTierRungs;

  useEffect(() => {
    authToken.current = auth?.idToken; // Update the ref with the latest value of auth?.idToken
  }, [auth?.idToken]);

  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const promoData = await getPromoById(promoId, authToken.current);
        setPromo(promoData);
      } catch (error) {
        console.error('Error fetching promo data:', error);
      }
    };

    fetchPromo();
  }, [promoId]);

  useEffect(() => {
    if (!promo) {
      return;
    }

    const nextVipPromoKind = promo.vipPromoKind || VipPromoKind.TIER;
    setPromoSettings({
      vipPromoKind: nextVipPromoKind,
      vipTier:
        promo.vipTier || (nextVipPromoKind === VipPromoKind.LEADERBOARD ? VipTier.ALL : VipTier.BD),
    });
    setPromoSettingsError('');
  }, [promo]);

  const fetchPromoShopItems = useCallback(async () => {
    setLoading(true);
    try {
      const items = await getPromoShopItems(promoId, authToken.current);
      setShopItems(items ?? []);
    } catch (error) {
      console.error('Error fetching promo shop items:', error);
    }
    setLoading(false);
  }, [promoId]);

  useEffect(() => {
    if (supportsShopItems) {
      fetchPromoShopItems();
    }
  }, [supportsShopItems, fetchPromoShopItems]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchPromoShopItems();
    setLoading(false);
  };

  const handleAddShopItem = async newShopItem => {
    const itemPayload = { ...newShopItem, promoId: promo.promoId };
    const validationError = validateLeaderboardRanks(
      [...shopItems, itemPayload],
      promo.vipPromoKind
    );

    if (validationError) {
      return { success: false, error: validationError };
    }

    try {
      const newItem = await createPromoShopItem(promo.promoId, itemPayload, authToken.current);
      setShopItems(prevItems => [...prevItems, newItem]);
      return { success: true };
    } catch (error) {
      console.error('Error creating promo shop item:', error);
      const backendMessage = error?.response?.data?.message;
      return { success: false, error: backendMessage || 'Failed to add shop item.' };
    }
  };

  const handleVipPromoKindChange = event => {
    const nextVipPromoKind = event.target.value;
    setPromoSettings(prevSettings => ({
      ...prevSettings,
      vipPromoKind: nextVipPromoKind,
      vipTier: nextVipPromoKind === VipPromoKind.LEADERBOARD ? VipTier.ALL : VipTier.BD,
    }));
    setPromoSettingsError('');
  };

  const handlePromoSettingsSave = async () => {
    const validationError = validateLeaderboardRanks(shopItems, promoSettings.vipPromoKind);

    if (validationError) {
      setPromoSettingsError(validationError);
      return;
    }

    const payload = {
      vipPromoKind: promoSettings.vipPromoKind,
      vipTier: promoSettings.vipTier,
    };

    setPromoSettingsSaving(true);
    setPromoSettingsError('');
    try {
      const updatedPromo = await updatePromo(promo.promoId, payload, authToken.current);
      setPromo(prevPromo => ({ ...prevPromo, ...payload, ...(updatedPromo ?? {}) }));
    } catch (error) {
      console.error('Error updating promo VIP settings:', error);
      setPromoSettingsError('Failed to save VIP promo settings.');
    } finally {
      setPromoSettingsSaving(false);
    }
  };

  const LoadingSpinner = () => (
    <Spinner animation="border" size="sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  );

  const isVip = promo?.promoType === PromoType.VIP;
  const isWaitlist = promo?.promoType === PromoType.WAITLIST;

  // Track C: grants use the Cap campaign sealed on each WAVE at create.
  // The promo field is a leftover Atlantis default only — no Change/Save.
  const rfpCampaignId = promo?.rfpCampaignId ?? null;
  const [rfpCampaign, setRfpCampaign] = useState(null);
  useEffect(() => {
    if (!rfpCampaignId) {
      setRfpCampaign(null);
      return undefined;
    }
    let cancelled = false;
    getCampaign(rfpCampaignId)
      .then(c => {
        if (!cancelled) setRfpCampaign(c ?? null);
      })
      .catch(() => {
        if (!cancelled) setRfpCampaign(null);
      });
    return () => {
      cancelled = true;
    };
  }, [rfpCampaignId]);

  // Leaderboard promos show prize items in podium order (1 = 🥇 first);
  // unranked/legacy items sink to the end. Other promos keep fetch order.
  const displayedShopItems = isPersistedLeaderboardPromo
    ? [...shopItems].sort(
        (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER)
      )
    : shopItems;

  // Tier-themed colors for the qualifying-volume callout: Big Degen = blue,
  // VIP = crimson, VVIP = purple (Admin's art pack, 2026-08-19 — Big Degen moved
  // off gold). Keep in step with vipBadges.js VIP_TIER_ACCENT.
  const vipTierTheme = {
    BD: { bg: '#eff4ff', border: '#d6e2ff', accent: '#2b5fd9' },
    VIP: { bg: '#fef2f2', border: '#fecaca', accent: '#c0392b' },
    VVIP: { bg: '#eef2ff', border: '#e0e7ff', accent: '#4338ca' },
  }[promo?.vipTier] ?? { bg: '#f1f3f5', border: '#dee2e6', accent: '#495057' };

  return (
    <div className="container">
      {promo ? (
        <div>
          <PromoBreadcrumb currentName={promo?.promoName} promoId={promo.promoId} />
          <div className="card border-0 shadow-sm rounded-4 my-4">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <h2 className="mb-2 fw-semibold text-dark">{promo.promoName}</h2>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className={`badge ${getPromoTypeColor(promo.promoType)}`}>
                      {promo.promoType}
                    </span>
                    <span className={`badge ${getStatusColor(promo.status)}`}>{promo.status}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    <i className="fa-solid fa-calendar me-2" />
                    {showUtcClock(promo.startTime)} &ndash; {showUtcClock(promo.endTime)}
                  </div>
                  {isWaitlist && (
                    <div className="mt-2" style={{ fontSize: '0.9rem' }}>
                      <div className="text-muted">
                        <i className="fa-solid fa-ticket me-2" />
                        RFP campaign (legacy/default on this promo):{' '}
                        {rfpCampaignId ? (
                          <span className="text-dark">
                            {rfpCampaign?.name ?? rfpCampaignId}
                            {rfpCampaign?.name && (
                              <span className="text-muted"> ({rfpCampaignId})</span>
                            )}
                            {rfpCampaign?.expireAt && (
                              <span className="text-muted">
                                {' '}
                                · redeem by {showUtcClock(rfpCampaign.expireAt)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">
                            none — new waves pick their Cap campaign at create
                          </span>
                        )}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                        Wave grants use the campaign sealed on each wave at create. This promo field
                        is not an editor — rebind-on-promo risk is gone for new waves.
                      </div>
                    </div>
                  )}
                </div>
                {isVip && promo.vipTier && (
                  <div
                    className="rounded-4 px-4 py-3 text-center"
                    style={{
                      background: vipTierTheme.bg,
                      border: `1px solid ${vipTierTheme.border}`,
                      minWidth: '180px',
                    }}
                  >
                    <div
                      className="text-uppercase text-muted fw-semibold mb-1"
                      style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}
                    >
                      Qualifying Volume
                    </div>
                    <div
                      className="fw-bold"
                      style={{ fontSize: '1.6rem', color: vipTierTheme.accent, lineHeight: 1.1 }}
                    >
                      {VipTierThresholdsSol[promo.vipTier] ?? '—'}{' '}
                      <span style={{ fontSize: '1rem' }}>SOL</span>
                    </div>
                    <div className="mt-2">
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: vipTierTheme.accent,
                          color: '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          padding: '0.4em 0.85em',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {VipTierLabel[promo.vipTier] ?? promo.vipTier} Tier
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {isVipPromo && (
              <div className="mt-3 pt-3 border-top">
                <div className="row g-3 align-items-end">
                  <Form.Group className="col-md-4" controlId="vipPromoKind">
                    <Form.Label>VIP Promo Kind</Form.Label>
                    <Form.Select
                      value={promoSettings.vipPromoKind}
                      onChange={handleVipPromoKindChange}
                      disabled={shopWritesLocked}
                    >
                      <option value={VipPromoKind.TIER}>Tier</option>
                      <option value={VipPromoKind.LEADERBOARD}>Leaderboard</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-4" controlId="vipTier">
                    <Form.Label>VIP Tier</Form.Label>
                    <Form.Select
                      value={promoSettings.vipTier}
                      onChange={e => {
                        setPromoSettings(prevSettings => ({
                          ...prevSettings,
                          vipTier: e.target.value,
                        }));
                        setPromoSettingsError('');
                      }}
                      disabled={shopWritesLocked}
                    >
                      {vipTierOptions.map(tier => (
                        <option key={tier} value={tier}>
                          {tier === VipTier.ALL ? 'All VIP Tiers' : VipTierLabel[tier] ?? tier}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  {!shopWritesLocked && (
                    <div className="col-md-auto">
                      <Button
                        variant="primary"
                        onClick={handlePromoSettingsSave}
                        disabled={promoSettingsSaving}
                      >
                        {promoSettingsSaving ? <LoadingSpinner /> : 'Save VIP Settings'}
                      </Button>
                    </div>
                  )}
                </div>
                {promoSettingsError && <div className="text-danger mt-2">{promoSettingsError}</div>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <LoadingSpinner />
      )}
      {promo && supportsShopItems && (
        <>
          <div className="d-flex justify-content-between mb-2">
            <h4>{isWaitlist ? 'Waitlist Prizes' : isVip ? 'Reward Items' : 'Shop Items'}</h4>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-dark me-2"
                onClick={() => navigate(`/promos/${promoId}/search`)}
              >
                <i className="fa-solid fa-search" /> Search Wallet
              </button>
              {!shopWritesLocked && (
                <AddShopItemButton
                  onAdd={handleAddShopItem}
                  token={auth?.idToken}
                  isVip={isVip}
                  isWaitlist={isWaitlist}
                  showRank={isPersistedLeaderboardPromo}
                  rankOptions={getRankOptions(promo?.rankN ?? shopItems.length + 1)}
                />
              )}
              <Button
                variant="secondary"
                className="mx-2"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : 'Refresh'}
              </Button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : shopItems.length > 0 ? (
            <div>
              {displayedShopItems.map(item => (
                <ExpandableShopItem
                  key={item.itemId}
                  shopItem={item}
                  token={auth?.idToken}
                  refreshShopItems={fetchPromoShopItems}
                  isVip={isVip}
                  isWaitlist={isWaitlist}
                  promo={promo}
                  shopItems={shopItems}
                  shopEnableOnly={shopEnableOnly}
                  showItemEnable={showItemEnable}
                  shopReadOnly={shopReadOnly}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted">
              No {isWaitlist ? 'prizes' : isVip ? 'reward items' : 'shop items'} yet.
              {!shopWritesLocked && ' Add one to define the reward.'}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Promo;
